import React, { useState, useEffect } from 'react';

// MUI
import { Grid, Button, TextField, Typography, IconButton, Tooltip } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';

// MUI Icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Util
import GenerateToken from '../../Component/GenerateToken';
import W86PlaceholderTextField from '../../W86Components/W86PlaceholderTextField';
import checkKey from '../../../utils/CheckKey';
import replacePlaceholderValue from '../../../utils/ReplacePlaceholderValue';
import config from '../../../config';
import useProjectStore from '../../ProjectCanvas/useProjectStore';

function OpenEndpointStep(props) {
  const [hasToken, setHasToken] = useState(props.hasToken);
  const [errorMessage, setErrorMessage] = useState('');
  const [tokenRegenerated, setTokenRegenerated] = useState(false);
  const [copied, setCopied] = useState(false);

  // Check if workflow is published
  const isPublished = useProjectStore.getState().publishedVersion && useProjectStore.getState().publishedVersion > 0;

  useEffect(() => {
    if (!props.componentStore.data) return;

    // If not correct structure, set auto placeholder
    if (
      !props.componentStore.data.samplePlaceholders ||
      props.componentStore.data.samplePlaceholders.length !== 1
    ) {
      props.componentStore.setRawResponsePH(props.componentStore.keyAuto());
    }
  }, []);

  const changeTokenStatus = (status, wasRegenerated = false) => {
    setHasToken(status);
    props.changeTokenStatus(status);
    
    // If token was regenerated (not first generation), show warning
    if (wasRegenerated && status) {
      setTokenRegenerated(true);
    }
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(config.WEBHOOK_URL + `${props.componentId}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Grid container spacing={2}>
      {/* Status Banner */}
      <Grid item xs={12}>
        {tokenRegenerated ? (
          <div className="webhook-status-banner warning">
            <WarningAmberIcon className="status-banner-icon" />
            <div className="status-banner-content">
              <span className="status-banner-title">Token Regenerated - Republish Required</span>
              <span className="status-banner-subtitle">
                You have regenerated the security token. The webhook will not accept requests with the new token until the workflow is republished.
              </span>
            </div>
          </div>
        ) : isPublished && hasToken ? (
          <div className="webhook-status-banner active">
            <CheckCircleIcon className="status-banner-icon" />
            <div className="status-banner-content">
              <span className="status-banner-title">Webhook Active</span>
              <span className="status-banner-subtitle">
                This webhook endpoint is live and accepting incoming requests
              </span>
            </div>
          </div>
        ) : (
          <div className="webhook-status-banner inactive">
            <ErrorIcon className="status-banner-icon" />
            <div className="status-banner-content">
              <span className="status-banner-title">Webhook Inactive</span>
              <span className="status-banner-subtitle">
                {!hasToken
                  ? 'Generate a security token and publish the workflow to activate this webhook'
                  : 'Publish the workflow to activate this webhook endpoint'}
              </span>
            </div>
          </div>
        )}
      </Grid>

      {/* Production Endpoint Section */}
      <Grid item xs={12} className="webhook-section">
        <Typography className="webhook-section-label">Production Endpoint URL</Typography>
        <Typography className="webhook-section-helper">
          Direct your API to send payloads to this URL. Include the security token in the request header.
        </Typography>
        <TextField
          value={config.WEBHOOK_URL + `${props.componentId}`}
          variant="outlined"
          disabled={true}
          fullWidth
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={copied ? 'Copied!' : 'Copy URL'}>
                  <IconButton onClick={handleCopyUrl} edge="end" size="small">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
            sx: { fontFamily: 'monospace', fontSize: '13px' }
          }}
        />
      </Grid>

      {/* Output Placeholder Section */}
      <Grid item xs={12} className="webhook-section">
        <Typography className="webhook-section-label">Output Placeholder</Typography>
        <Typography className="webhook-section-helper">
          The entire raw payload will be stored in this placeholder. Use a JSON parser or AI component to extract specific values.
        </Typography>
        <W86PlaceholderTextField
          value={props.componentStore.data.samplePlaceholders[0].key}
          error={errorMessage}
          helperText={errorMessage}
          placeholderText="Enter placeholder name"
          handleChange={event => {
            const val = replacePlaceholderValue(event.target.value);

            // Check the placeholder key
            const check = checkKey(val);
            if (check && check[0] && check.length > 1) {
              setErrorMessage(check[1]);
              return;
            } else setErrorMessage('');

            props.componentStore.setRawResponsePH(val);
          }}
          customStyleProps={{ maxWidth: 400 }}
        />
      </Grid>

      {/* Security Token Section */}
      <Grid item xs={12} className="webhook-section">
        <Typography className="webhook-section-label">Security Token</Typography>
        <Typography className="webhook-section-helper">
          Generate a token and include it in the request header (recommended) or as a URL parameter.{' '}
          <a
            href="https://docs.workflow86.com/docs/components/webhook_import/#how-to-set-the-token"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#1976d2', textDecoration: 'none' }}
          >
            Learn more
          </a>
        </Typography>
        
        <GenerateToken
          hasToken={status => {
            // Check if this was a regeneration (had token before)
            const wasRegenerated = hasToken && status;
            changeTokenStatus(status, wasRegenerated);
          }}
          tokenStatus={props.hasToken}
          name={props.name}
          component_id={props.componentId}
          isAlignCenter={false}
        />

        {/* Token Status Messages */}
        {tokenRegenerated && (
          <div className="webhook-token-warning">
            <WarningAmberIcon className="warning-icon" />
            <div className="warning-content">
              <span className="warning-title">Action Required: Republish Workflow</span>
              <span className="warning-text">
                You have generated a new token. The webhook will reject requests using the old token. 
                Republish this workflow and update your external system with the new token.
              </span>
            </div>
          </div>
        )}

        {!hasToken && (
          <div className="webhook-info-box">
            <InfoOutlinedIcon className="info-icon" />
            <span className="info-text">
              A security token is required to protect your webhook endpoint from unauthorized requests.
            </span>
          </div>
        )}

        {hasToken && !tokenRegenerated && (
          <div className="webhook-info-box">
            <InfoOutlinedIcon className="info-icon" />
            <span className="info-text">
              A token has been generated. If you've lost it, you can regenerate a new one - but remember to update your external system and republish the workflow.
            </span>
          </div>
        )}
      </Grid>

      {/* Back Button Section */}
      <Grid item xs={12}>
        <Button
          onClick={() => props.onChangeState(0, 0)}
          variant="outlined"
        >
          Back
        </Button>
      </Grid>
    </Grid>
  );
}

export default OpenEndpointStep;
